import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { buildStack, stackExtent, overlayRuns, SEGMENT_ORDER, OVERLAY_LABEL } from './financialsStack';
import type { StackBar, StackView } from './financialsStack';
import type { IncomeStatementRow, BalanceSheetRow, CashFlowRow } from '../../types/tickerDetail';
import { useChartSize } from './useChartSize';

/**
 * Stacked bars for one financial statement.
 *
 * Reads the same rows as the table below it, so the two cannot disagree. All
 * arithmetic lives in financialsStack.ts, which is tested; this file is
 * rendering only.
 */

// Palette from .claude/skills/charting.md. Raw hex because D3 takes colours as
// strings and cannot resolve a Tailwind token — these do NOT track theme
// changes, so a palette rename has to update them by hand.
const COLORS = {
  grid: '#2B2B2B', // dark-border
  axis: '#C7C2B8', // text-muted
  marker: '#C2A14E', // accent-yellow
  overlay: '#F4F2ED', // bone — the trend line reads over every segment beneath it
  // Cost segments recede, earnings segments advance. Deliberately not a
  // categorical scheme: the reader should see "most of revenue is cost" at a
  // glance, which needs a value ramp rather than equally loud hues.
  cogs: '#3F3F3D',
  opex: '#61615E',
  operatingIncome: '#1C76A2', // bull-blue
  liabilities: '#61615E',
  equity: '#1C76A2',
  operatingCashFlow: '#1C76A2',
  capex: '#D8483B', // bear-red — cash leaving the business
} as const;

const HEIGHT = 380;
const MARGIN = { top: 12, right: 16, bottom: 28, left: 56 };

/** Compact money for axis ticks and labels. */
function money(n: number): string {
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1e12) return `${sign}$${(abs / 1e12).toFixed(1)}T`;
  if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(1)}K`;
  return `${sign}$${abs.toFixed(0)}`;
}

/**
 * Period end as a short axis label.
 *
 * Annual periods are named by year alone; quarters carry the year they fall
 * in, since eight quarters span two of them.
 */
function periodLabel(date: string, period: 'annual' | 'quarter'): string {
  const [y, m] = date.split('-');
  if (period === 'annual') return y;
  const q = Math.ceil(Number(m) / 3);
  return `Q${q} '${y.slice(2)}`;
}

interface Props {
  rows: IncomeStatementRow[] | BalanceSheetRow[] | CashFlowRow[];
  view: StackView;
  period: 'annual' | 'quarter';
}

interface Hover {
  x: number;
  y: number;
  bar: StackBar;
  /**
   * The chart this was captured on. Switching sub-tab or period redraws the
   * SVG and the bar underneath the cursor is gone, so a hover carrying a
   * different key is stale and simply not rendered. Derived rather than
   * cleared in an effect: an effect fires after paint, leaving one frame
   * where a tooltip describes bars that are no longer on screen.
   */
  key: string;
}

export function FinancialsChart({ rows, view, period }: Props): React.ReactElement {
  const { ref: containerRef, width } = useChartSize<HTMLDivElement>();
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [hover, setHover] = useState<Hover | null>(null);

  const bars = buildStack(rows, view);
  const chartKey = `${view}:${period}`;

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    // Clear-and-redraw rather than a data join: at eight bars redrawn only on
    // data or resize, the join's bookkeeping buys nothing and is easy to get
    // subtly wrong.
    svg.selectAll('*').remove();

    if (!width || !bars.length) return;

    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = HEIGHT - MARGIN.top - MARGIN.bottom;
    if (innerW <= 0) return;

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left},${MARGIN.top})`);

    const x = d3
      .scaleBand<string>()
      .domain(bars.map((b) => b.date))
      .range([0, innerW])
      .padding(0.28);

    const [min, max] = stackExtent(bars);
    const y = d3.scaleLinear().domain([min, max]).nice().range([innerH, 0]);

    // Gridlines behind everything, drawn first so bars sit on top of them.
    g.append('g')
      .attr('class', 'grid')
      .call(d3.axisLeft(y).ticks(5).tickSize(-innerW).tickFormat(() => ''))
      .call((sel) => sel.select('.domain').remove())
      .selectAll('line')
      .attr('stroke', COLORS.grid);

    // Value axis.
    g.append('g')
      .call(d3.axisLeft(y).ticks(5).tickFormat((d) => money(d as number)))
      .call((sel) => sel.select('.domain').remove())
      .call((sel) => sel.selectAll('line').remove())
      .selectAll('text')
      .attr('fill', COLORS.axis)
      .attr('font-size', 10);

    // Period axis along the baseline, not the bottom of the frame — with a
    // negative bar present those are different places, and labels under the
    // frame would sit a long way from the bars they name.
    g.append('g')
      .attr('transform', `translate(0,${y(0)})`)
      .call(d3.axisBottom(x).tickFormat((d) => periodLabel(d as string, period)))
      .call((sel) => sel.select('.domain').attr('stroke', COLORS.grid))
      .call((sel) => sel.selectAll('line').remove())
      .selectAll('text')
      .attr('fill', COLORS.axis)
      .attr('font-size', 10);

    for (const bar of bars) {
      const bx = x(bar.date);
      if (bx == null) continue;

      const group = g.append('g');

      for (const seg of bar.segments) {
        // y1 is the far edge, which is below y0 for a negative segment, so the
        // rect's top is whichever of the two sits higher on screen.
        const top = Math.min(y(seg.y0), y(seg.y1));
        const h = Math.abs(y(seg.y1) - y(seg.y0));
        group
          .append('rect')
          .attr('x', bx)
          .attr('y', top)
          .attr('width', x.bandwidth())
          .attr('height', h)
          .attr('fill', COLORS[seg.key as keyof typeof COLORS] ?? COLORS.opex);
      }

      // The marker — net income, total assets, free cash flow — as a rule
      // across the bar. It is a different quantity from the stack beneath it,
      // so it reads as a line rather than another block of colour.
      if (bar.marker) {
        const my = y(bar.marker.value);
        group
          .append('line')
          .attr('x1', bx - 2)
          .attr('x2', bx + x.bandwidth() + 2)
          .attr('y1', my)
          .attr('y2', my)
          .attr('stroke', COLORS.marker)
          .attr('stroke-width', 2);
      }

      // One transparent hit area per bar rather than per segment: hovering
      // anywhere on the column should show the whole period's breakdown.
      group
        .append('rect')
        .attr('x', bx)
        .attr('y', 0)
        .attr('width', x.bandwidth())
        .attr('height', innerH)
        .attr('fill', 'transparent')
        .style('cursor', 'pointer')
        .on('mouseenter', () =>
          setHover({ x: bx + x.bandwidth() / 2 + MARGIN.left, y: MARGIN.top, bar, key: chartKey })
        )
        .on('mouseleave', () => setHover(null));
    }

    // The overlay last, so the line and its points sit above every bar rather
    // than being buried by the one drawn after them.
    const runs = overlayRuns(bars);
    if (runs.length) {
      const cx = (index: number): number => (x(bars[index].date) ?? 0) + x.bandwidth() / 2;
      const line = d3
        .line<{ index: number; value: number }>()
        .x((d) => cx(d.index))
        .y((d) => y(d.value));

      for (const run of runs) {
        if (run.length > 1) {
          g.append('path')
            .attr('d', line(run))
            .attr('fill', 'none')
            .attr('stroke', COLORS.overlay)
            .attr('stroke-width', 1.5);
        }
        // Points on every value, including single-point runs — an isolated
        // period either side of a gap would otherwise draw nothing at all.
        for (const point of run) {
          g.append('circle')
            .attr('cx', cx(point.index))
            .attr('cy', y(point.value))
            .attr('r', 2.5)
            .attr('fill', COLORS.overlay);
        }
      }
    }
  }, [bars, width, chartKey, period]);

  const activeHover = hover?.key === chartKey ? hover : null;

  if (!bars.length) {
    return <p className="text-gray-400 text-sm py-6">No data to chart</p>;
  }

  return (
    <div ref={containerRef} className="relative w-full">
      <svg ref={svgRef} width={width} height={HEIGHT} />

      {activeHover && (
        <div
          className="pointer-events-none absolute z-10 rounded border border-dark-border bg-dark-surface px-2.5 py-2 text-xs shadow-lg"
          style={{
            left: Math.min(Math.max(activeHover.x - 80, 0), Math.max(width - 170, 0)),
            top: activeHover.y,
          }}
        >
          <div className="text-white font-semibold mb-1 font-numeric">
            {periodLabel(activeHover.bar.date, period)}
          </div>
          {/* The total first — it is what the bar's height means, and the
              figure a reader reaches for before any breakdown of it. */}
          {activeHover.bar.total && (
            <div className="flex items-center gap-2 whitespace-nowrap mb-1 pb-1 border-b border-dark-border">
              <span className="text-gray-400">{activeHover.bar.total.label}</span>
              <span className="ml-auto text-white font-numeric font-semibold">
                {money(activeHover.bar.total.value)}
              </span>
            </div>
          )}
          {activeHover.bar.segments.map((s) => (
            <div key={s.key} className="flex items-center gap-2 whitespace-nowrap">
              <span
                className="inline-block w-2 h-2 rounded-sm shrink-0"
                style={{ background: COLORS[s.key as keyof typeof COLORS] ?? COLORS.opex }}
              />
              <span className="text-gray-400">{s.label}</span>
              <span className="ml-auto text-gray-200 font-numeric">{money(s.value)}</span>
            </div>
          ))}
          {activeHover.bar.overlay != null && OVERLAY_LABEL[view] && (
            <div className="flex items-center gap-2 whitespace-nowrap">
              <span
                className="inline-block w-2 h-0.5 shrink-0"
                style={{ background: COLORS.overlay }}
              />
              <span className="text-gray-400">{OVERLAY_LABEL[view]}</span>
              <span className="ml-auto text-gray-200 font-numeric">
                {money(activeHover.bar.overlay)}
              </span>
            </div>
          )}
          {activeHover.bar.marker && (
            <div className="flex items-center gap-2 whitespace-nowrap mt-1 pt-1 border-t border-dark-border">
              <span
                className="inline-block w-2 h-0.5 shrink-0"
                style={{ background: COLORS.marker }}
              />
              <span className="text-gray-400">{activeHover.bar.marker.label}</span>
              <span className="ml-auto text-gray-200 font-numeric">
                {money(activeHover.bar.marker.value)}
              </span>
            </div>
          )}
        </div>
      )}

      <Legend view={view} bars={bars} />
    </div>
  );
}

/** Colour key, naming the segments actually drawn. */
function Legend({ view, bars }: { view: StackView; bars: StackBar[] }): React.ReactElement {
  // Read the labels off the bars rather than a static map so the legend can
  // never name a segment the data did not produce.
  const seen = new Map<string, string>();
  for (const bar of bars) {
    for (const seg of bar.segments) if (!seen.has(seg.key)) seen.set(seg.key, seg.label);
  }
  const marker = bars.find((b) => b.marker)?.marker?.label;
  const overlay = bars.some((b) => b.overlay != null) ? OVERLAY_LABEL[view] : null;

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-[11px] text-gray-400">
      {SEGMENT_ORDER[view]
        .filter((key) => seen.has(key))
        .map((key) => (
          <span key={key} className="flex items-center gap-1.5">
            <span
              className="inline-block w-2.5 h-2.5 rounded-sm"
              style={{ background: COLORS[key as keyof typeof COLORS] }}
            />
            {seen.get(key)}
          </span>
        ))}
      {marker && (
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-0.5" style={{ background: COLORS.marker }} />
          {marker}
        </span>
      )}
      {overlay && (
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-0.5" style={{ background: COLORS.overlay }} />
          {overlay}
        </span>
      )}
    </div>
  );
}
